import { APIEndpoints } from '@/@admin/types/constants';
import { AdminToolsParams, Client, UpdateResponse } from '@/@admin/types/entities';
import { buildCacheKey } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for client management
 */
export class ClientService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async getClients(params: AdminToolsParams): Promise<Client[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchClients', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data: response } = await this.axiosInstance.get<{
                hasError: boolean;
                message?: string;
                data?: Client[];
            }>(`${APIEndpoints.ORGS}/all`, { params });

            // Ensure we have data
            if (response?.hasError || !Array.isArray(response?.data)) {
                const msg = response?.message ?? 'No clients data received from the server';
                tracking.errorWithSource(new Error(msg), 'studio.ClientService.getClients');
                throw new Error(ERROR_MESSAGES.API_ERROR(msg));
            }

            // Cache the response data
            this.setCachedData(cacheKey, response.data);
            return response.data;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get clients: ${errorMessage}`),
                'studio.ClientService.getClients',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get clients: ${errorMessage}`));
        }
    }

    async updateClient(clientId: number, data: Partial<Client>): Promise<UpdateResponse> {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.ORGS}/${clientId}`,
                data,
            );
            // Ensure we have a valid response
            if (response.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'No client data received from the server'),
                    'studio.ClientService.updateClient',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        response.message || 'No client data received from the server',
                    ),
                );
            }

            this.clearCache();
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update client: ${errorMessage}`),
                'studio.ClientService.updateClient',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to update client: ${errorMessage}`));
        }
    }
}

export const clientService = new ClientService();
